import { isEmailValid } from '@agent/core/channel-digit/mail/util/mail-util';
import { SearchService } from '@agent/core/search/search.service';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { MailComment } from '@commons/history/history-mail/mail-comment/models/mail-comment';
import { IEpticaExctractAttachment, IEpticaExtractAttachmentAction } from '@commons/history/history-mail/models/eptica-extract';
import { isEmpty } from '@commons/util/helper/helper';
import { optToPromise } from '@commons/util/utilities/option';
import { ng1Destroy } from '@commons/util/utilities/rx';
import { fromJson, IDeferred, ILogService, IPromise, IQService, isDefined, ITimeoutService } from 'angular';
import { i18n } from 'i18next';
import {
  catchError,
  concatMap,
  delay as observableDelay,
  EMPTY,
  filter,
  first,
  fromEvent,
  map,
  mergeMap,
  Observable,
  of,
  range,
  retryWhen,
  switchMap,
  tap,
} from 'rxjs';

import {
  BookingFacade,
  ChannelDirection,
  ChannelType,
  EpticaEnterpriseAgentExposed,
  None,
  Option,
  ProfileService,
  ToastClass,
  ToastType,
} from '@contact/upgrade';

import { EpticaButtonActionTypes, EpticaButtonTypes, EpticaData, EpticaDataRequest, EpticaDropZoneElements } from './models/eptica-data';
import { EPTICA_LOGIN_FRAME_NAME } from './models/eptica.request.interfaces';
import { ServiceWrapper } from './models/service-wrapper';

export const DEFAULT_TOAST_DURATION = 5000;
export const LEGACY_UNKNOWN_CONTACT = 'INCONNU';
export const UNKNOWN_CONTACT = '-';
export const REJECTION_PENDING_ASK_FOR_HELP = 'REJECTION_PENDING_ASK_FOR_HELP';
export const REPLACED_BUTTON_ID_PREFIX = 'cng';

export const REQUEST_HISTORY_MODE_FULL = 'FULL';
export const REQUEST_HISTORY_MODE_CONVERSATIONAL = 'CONVERSATIONAL';

export type RequestHistoryMode = 'FULL' | 'CONVERSATIONAL';
export type KindOfForward = 'external' | 'agent';

const CONTACT_CSS_CLASS = 'contact';
const OUTGOING_CSS_CLASS = 'cngOutgoing';
const RESPONSIVE_CSS_CLASS = 'responsive';
const EMAIL_CUSTOMER_FIELD = 'EMail';
export const IS_EPTICA_REPONSIVE = false;
export const EMAIL_LIST_REGEXP =
  /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/gi;

export class Select2Data {
  id: string;
  text: string;
}

const identity = <T>(t: T) => t;

export class EpticaHelper {
  static listenerNb = 0;

  static hasAttribute(el, attrName) {
    return typeof el.attributes[attrName] !== 'undefined';
  }

  static addAttribute(el, attrName, attrValue) {
    el.setAttribute(attrName, attrValue);
  }

  static toggleAttr(el, attribute, value) {
    if (value) {
      // to add
      if (!this.hasAttribute(el, attribute)) {
        this.addAttribute(el, attribute, '');
      }
    } else {
      // to remove
      if (this.hasAttribute(el, attribute)) {
        el.removeAttribute(attribute);
      }
    }
  }

  static getEpticaData(frame: HTMLIFrameElement): Option<EpticaData> {
    const epticaData = frame.contentWindow && frame.contentWindow['epticaData'];
    return Option.of(epticaData).map(data => {
      let pageContextOpt = Option.of(data.pageContext);
      return {
        pageName: data.pageName,
        pageContext: {
          account: pageContextOpt.map(context => EpticaHelper.getEpticaDataAccount(context.account)).getOrElse(undefined),
          request: pageContextOpt.flatMap(context => EpticaHelper.getEpticaDataRequest(context)),
          requests: pageContextOpt
            .flatMap(context => Option.of(context.requests))
            .getOrElse([])
            .filter(request => EpticaHelper.getEpticaDataRequest(request).isDefined())
            .map(request => EpticaHelper.getEpticaDataRequest(request).get()),
        },
      };
    });
  }

  static getAccount(epticaData: EpticaData): string | undefined {
    return epticaData.pageContext.account;
  }

  static getAssignedAccountId(epticaData: EpticaData): string | undefined {
    if (epticaData.pageContext.request.isEmpty() || !epticaData.pageContext.request.get().accountResponsible) {
      return undefined;
    }
    return epticaData.pageContext.request.get().accountResponsible;
  }

  static isAssignedToCurrentUser(epticaData: EpticaData): boolean {
    const accountId = EpticaHelper.getAccount(epticaData);
    const assignedAccountId = EpticaHelper.getAssignedAccountId(epticaData);
    return !!accountId && !!assignedAccountId && accountId === assignedAccountId;
  }

  static submitLoginForm(url: string, username: string, password: string): void {
    const form = document.createElement('form');
    form.action = `${url}/logincheck.action`;
    form.target = EPTICA_LOGIN_FRAME_NAME;
    form.method = 'post';

    const usernameInput = document.createElement('input');
    usernameInput.type = 'hidden';
    usernameInput.name = 'j_username';
    usernameInput.value = username;
    form.appendChild(usernameInput);

    const passwordInput = document.createElement('input');
    passwordInput.type = 'hidden';
    passwordInput.name = 'j_password';
    passwordInput.value = password;
    form.appendChild(passwordInput);

    const body = document.getElementsByTagName('body').item(0);
    if (body) {
      body.appendChild(form);
    }

    form.submit();
    if (form && form.remove) form.remove();
    // ieFix, form == null sous IE9
  }

  static setContactClass(frame: HTMLIFrameElement): void {
    if (frame.contentWindow) {
      const htmlEl = frame.contentWindow.document.getElementsByTagName('html').item(0);
      if (htmlEl) {
        let html = $(htmlEl);
        if (!html.hasClass(CONTACT_CSS_CLASS)) {
          html.toggleClass(CONTACT_CSS_CLASS, true);
        }
        if (IS_EPTICA_REPONSIVE && !html.hasClass(RESPONSIVE_CSS_CLASS)) {
          html.toggleClass(RESPONSIVE_CSS_CLASS, true);
        }
      }
    }
  }

  static addCustomClass(frame: HTMLIFrameElement, customClass: string): void {
    if (frame.contentWindow) {
      const htmlEl = frame.contentWindow.document.getElementsByTagName('html').item(0);
      if (htmlEl) {
        let html = $(htmlEl);
        html.addClass(customClass);
      }
    }
  }

  /**
   * Add custom class on some "unmarked" element in order to make it targetable with css
   * without relying on bootstrap classes that are prone to change anytime
   * @param frame the Eptica frame the content of which we want to modify
   */
  static addClassToEditorFormContainer(frame: HTMLIFrameElement): void {
    if (frame.contentWindow) {
      const editorElement = frame.contentWindow.document.querySelector('#answer_editor');
      if (editorElement) {
        $(editorElement).closest('.input_container.form-group').addClass('answer_editor_container');
      }
    }
  }

  static setOutgoingClass(frame: HTMLIFrameElement): void {
    if (frame.contentWindow) {
      const htmlEl = frame.contentWindow.document.getElementsByTagName('html').item(0);
      if (htmlEl) {
        let html = $(htmlEl);
        if (!html.hasClass(OUTGOING_CSS_CLASS)) {
          html.toggleClass(OUTGOING_CSS_CLASS, true);
        }
      }
    }
  }

  static onRequestClick(
    $log: ILogService,
    frame: HTMLIFrameElement,
    listener: (requestId: number, pageData: Option<EpticaData>) => void
  ): void {
    if (frame.contentWindow && frame.contentDocument) {
      let requestLinks = frame.contentDocument.getElementsByClassName('request_link');
      for (let i = 0; i < requestLinks.length; i++) {
        let link = requestLinks.item(i);
        if (link && link.parentElement) {
          link.parentElement.onclick = () => {
            if (link) {
              try {
                let requestId = parseInt(link.id.replace('request_', ''), 10);
                listener(requestId, EpticaHelper.getEpticaData(frame));
              } catch (e) {
                $log.error(`[eptica] Failed to extract request id from[${link.id}]`);
              }
            }
          };
          link.setAttribute('href', '#');
        }
      }
    }
  }

  // eptica >= 9.6
  static checkDefaultHasHtmlState(frame: HTMLIFrameElement) {
    if (frame.contentWindow && frame.contentDocument) {
      // for each message
      const framesContent = frame.contentDocument.getElementsByTagName('iframe');

      setTimeout(() => {
        // we have to wait until Eptica expands its first frame...
        for (let i = 0; !!framesContent && i < framesContent.length; i++) {
          const frameContent = $(framesContent[i]);
          const parentDiv = frameContent.parent();
          // check hasHtml attr
          if (parentDiv.attr('data-hasHTML') === 'false') {
            const switchableContent = frameContent.parents().eq(2);
            const switchItem = switchableContent.find('.switchable_content_switch_icon');
            const htmlLabelElement = switchableContent.find('.switch_label_html');
            // if switch doesn't match
            if (htmlLabelElement.hasClass('btn_html_on')) {
              switchItem.click();
            }
          }
        }
      }, 1000);
    }
  }

  static simulateClickOnSelection(
    frame: HTMLIFrameElement | HTMLElement,
    selector: string,
    bubules: boolean = false,
    cancelable: boolean = true
  ): void {
    let element;
    let frameDoc: Document;
    if (frame instanceof HTMLIFrameElement) {
      frameDoc = (frame as HTMLIFrameElement).contentWindow!.document;
      element = frameDoc.querySelector(selector);
    } else {
      frameDoc = frame.ownerDocument!;
      element = frame.querySelector(selector);
    }
    if (element) {
      if ((element as any).fireEvent) {
        (element as any).fireEvent('onclick');
      } else {
        let evObj = frameDoc.createEvent('Events');
        evObj.initEvent('click', bubules, cancelable);
        element.dispatchEvent(evObj);
      }
    }
  }

  static simulateClick(frame: HTMLIFrameElement, element: string | HTMLElement): void {
    let e: HTMLElement;
    if (typeof element === 'string') {
      e = this.getEpticaElementById(frame, element);
    } else {
      e = element;
    }
    if ((e as any).fireEvent) {
      (e as any).fireEvent('onclick');
    } else {
      $(e).trigger('click');
    }
  }

  static submitEpticaForm(frame: HTMLIFrameElement, elementId: string): void {
    let element: HTMLFormElement = this.getEpticaElementById(frame, elementId) as HTMLFormElement;
    if (element) {
      element.submit();
    }
  }

  static _waitLoad($q: IQService, frame: HTMLIFrameElement, checkFunc: (frame: HTMLIFrameElement) => boolean): IPromise<HTMLIFrameElement> {
    EpticaHelper.listenerNb = EpticaHelper.listenerNb + 1;
    let deferred = $q.defer<HTMLIFrameElement>();
    let listener = () => {
      frame.removeEventListener('load', listener);
      EpticaHelper.setContactClass(frame);
      if (checkFunc(frame)) {
        deferred.resolve(frame);
      } else {
        deferred.reject();
      }
    };
    frame.addEventListener('load', listener);
    return deferred.promise;
  }

  static _waitReady(
    $q: IQService,
    frame: HTMLIFrameElement,
    checkFunc: (frame: HTMLIFrameElement) => boolean
  ): IPromise<HTMLIFrameElement> {
    let deferred = $q.defer<HTMLIFrameElement>();
    let listener = event => {
      if (event && event.data === frame.name + 'Loaded') {
        frame.removeEventListener('message', listener);
        EpticaHelper.setContactClass(frame);
        if (checkFunc(frame)) {
          deferred.resolve(frame);
        } else {
          deferred.reject();
        }
      } else {
        deferred.reject();
      }
    };
    window.addEventListener('message', listener);
    return deferred.promise;
  }

  static doPostLoadActions(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement
  ): IPromise<EpticaData> {
    EpticaHelper.displayActionMessages(toastDeprecatedService, frame);
    EpticaHelper.addPrintAndPreviewButtons($i18next, sessionDeprecatedService, frame);
    EpticaHelper.customizeAnswerDisplay(frame, $i18next, sessionDeprecatedService, ChannelDirection.INCOMING);
    EpticaHelper.uniformUploadButtonForAskForHelp($i18next, frame);

    let optionOfEpticaData = EpticaHelper.getEpticaData(frame);
    if (!optionOfEpticaData.isDefined()) {
      $log.warn('The eptica data of the page has not been retrieved !!!');
    }
    return optToPromise(optionOfEpticaData, $q);
  }

  static waitForLoad(
    $q: IQService,
    frame: HTMLIFrameElement,
    checkFunc: (frame: HTMLIFrameElement) => boolean
  ): IPromise<HTMLIFrameElement> {
    let chkFunc = isDefined(checkFunc) ? checkFunc : () => true;
    return EpticaHelper._waitLoad($q, frame, chkFunc);
  }

  static waitForEarlyLoad(
    $q: IQService,
    frame: HTMLIFrameElement,
    checkFunc: (frame: HTMLIFrameElement) => boolean
  ): IPromise<HTMLIFrameElement> {
    // Pas au point, les toastDeprecatedServices sont affichés deux fois
    let defered = $q.defer<HTMLIFrameElement>();
    EpticaHelper._waitReady($q, frame, checkFunc).then(e => defered.resolve(e));
    EpticaHelper._waitLoad($q, frame, checkFunc).then(
      e => defered.resolve(e),
      e => defered.reject(e)
    );
    return defered.promise;
  }

  static waitForEpticaLoad(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement,
    checkFunc: (frame: HTMLIFrameElement) => boolean = () => true
  ): IPromise<EpticaData> {
    return EpticaHelper.waitForLoad($q, frame, checkFunc).then(f =>
      EpticaHelper.doPostLoadActions($q, $i18next, $log, toastDeprecatedService, sessionDeprecatedService, f)
    );
  }

  static waitForEpticaEarlyLoad(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement,
    checkFunc: (frame: HTMLIFrameElement) => boolean = () => true
  ): IPromise<EpticaData> {
    return EpticaHelper.waitForEarlyLoad($q, frame, checkFunc).then(f =>
      EpticaHelper.doPostLoadActions($q, $i18next, $log, toastDeprecatedService, sessionDeprecatedService, f)
    );
  }

  static simulateClickAndWaitForLoad(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement,
    elementId: string
  ): IPromise<EpticaData> {
    EpticaHelper.simulateClick(frame, elementId);
    return EpticaHelper.waitForEpticaLoad($q, $i18next, $log, toastDeprecatedService, sessionDeprecatedService, frame);
  }

  static simulateClickAndWaitForEarlyLoad(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement,
    elementId: string
  ): IPromise<EpticaData> {
    EpticaHelper.simulateClick(frame, elementId);
    return EpticaHelper.waitForEpticaEarlyLoad($q, $i18next, $log, toastDeprecatedService, sessionDeprecatedService, frame);
  }

  static submitFormAndWailtForLoad(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement,
    formElementId: string
  ): IPromise<EpticaData> {
    EpticaHelper.submitEpticaForm(frame, formElementId);
    return EpticaHelper.waitForEpticaLoad($q, $i18next, $log, toastDeprecatedService, sessionDeprecatedService, frame);
  }

  static overrideClosePendingAssist(frame: HTMLIFrameElement, confirmAction: () => void): void {
    EpticaHelper.replaceEpticaButton('askForHelpCancellation_promptPopupValidateButton', frame, confirmAction);
  }

  static confirmClosePendingAssist(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement,
    confirmButtonId: string
  ): IPromise<EpticaData> {
    return this.simulateClickAndWaitForLoad($q, $i18next, $log, toastDeprecatedService, sessionDeprecatedService, frame, confirmButtonId);
  }

  static sendAndCloseClick(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement
  ): IPromise<EpticaData> {
    return EpticaHelper._formAnswerCheckPendingAskForHelpAndSubmit(
      $q,
      $i18next,
      $log,
      toastDeprecatedService,
      sessionDeprecatedService,
      frame,
      '/serviceRequest/answerRequest.action'
    );
  }

  static sendAndDeferClick(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement
  ): IPromise<EpticaData> {
    return EpticaHelper._formAnswerCheckPendingAskForHelpAndSubmit(
      $q,
      $i18next,
      $log,
      toastDeprecatedService,
      sessionDeprecatedService,
      frame,
      '/serviceRequest/answerRequestAndPostpone.action'
    );
  }

  static askForReviewClick(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement
  ): IPromise<EpticaData> {
    return EpticaHelper._formAnswerCheckPendingAskForHelpAndSubmitReviewForm(
      $q,
      $i18next,
      $log,
      toastDeprecatedService,
      sessionDeprecatedService,
      frame,
      '/serviceRequest/askForReview.action'
    );
  }

  static simulateClickAndAttachmentsAndLoad(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement,
    elementId: string,
    forceNoAttachment: boolean = false
  ) {
    // TODO 6958 forceNoAttachment remove this condition and add attachment zone in transfer view
    const dropZone = (frame.contentWindow as any).Dropzone.forElement(EpticaDropZoneElements.ANSWER_REQUEST);
    if (forceNoAttachment || dropZone.getQueuedFiles().length === 0) {
      return EpticaHelper.simulateClickAndWaitForEarlyLoad(
        $q,
        $i18next,
        $log,
        toastDeprecatedService,
        sessionDeprecatedService,
        frame,
        elementId
      );
    } else {
      let promiseEpticaData = EpticaHelper.wrapEpticaHandleFileSubmitMethodAndWait($q, frame);
      EpticaHelper.simulateClick(frame, elementId);
      return promiseEpticaData;
    }
  }

  static submitWithAttachmentsAndWaitForLoad(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement,
    formId: string,
    action: string
  ) {
    const dropZone = (frame.contentWindow as any).Dropzone.forElement(EpticaDropZoneElements.ANSWER_REQUEST);
    if (dropZone.getQueuedFiles().length === 0) {
      return EpticaHelper.submitFormAndWailtForLoad($q, $i18next, $log, toastDeprecatedService, sessionDeprecatedService, frame, formId);
    } else {
      dropZone.options.url = EpticaHelper.getContextPath(frame) + action;
      let promiseEpticaData = EpticaHelper.wrapEpticaHandleFileSubmitMethodAndWait($q, frame);
      dropZone.processQueue();
      return promiseEpticaData;
    }
  }

  static cancelPendingActions(sw: ServiceWrapper, frame: HTMLIFrameElement, onCompleteAction: () => IPromise<any>): IPromise<any> {
    let form: HTMLFormElement = this.getEpticaElementById(frame, 'formAnswerRequest') as HTMLFormElement;
    let submitForm = (deferred: IDeferred<any>) =>
      onCompleteAction().then(
        () => deferred.resolve(),
        error => deferred.reject(error)
      );
    return EpticaHelper.checkPendingAskForHelpAndSubmit(sw.$q, frame, form, submitForm);
  }

  static _formAnswerCheckPendingAskForHelpAndSubmit(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement,
    action: string
  ): IPromise<EpticaData> {
    let form: HTMLFormElement = this.getEpticaElementById(frame, 'formAnswerRequest') as HTMLFormElement;
    let submitForm = (deferred: IDeferred<EpticaData>) => {
      form.target = '_self';
      form.action = EpticaHelper.getContextPath(frame) + action;
      EpticaHelper.submitWithAttachmentsAndWaitForLoad(
        $q,
        $i18next,
        $log,
        toastDeprecatedService,
        sessionDeprecatedService,
        frame,
        form.id,
        action
      ).then(
        result => deferred.resolve(result),
        error => deferred.reject(error)
      );
    };
    return EpticaHelper.checkPendingAskForHelpAndSubmit($q, frame, form, submitForm);
  }

  static _formAnswerCheckPendingAskForHelpAndSubmitReviewForm(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement,
    action: string
  ): IPromise<EpticaData> {
    let form: HTMLFormElement = this.getEpticaElementById(frame, 'formAnswerRequest') as HTMLFormElement;
    let submitForm = (deferred: IDeferred<EpticaData>) => {
      // copy field comment value in hidden field
      let hiddenCommentInput: HTMLInputElement = this.getEpticaElementBySelector(
        frame,
        'input[name=askForReviewComment]'
      ) as HTMLInputElement;
      let commentInput: HTMLInputElement = this.getEpticaElementById(frame, 'askForReview_promptField') as HTMLInputElement;
      hiddenCommentInput.value = commentInput.value;
      form.target = '_self';
      form.action = EpticaHelper.getContextPath(frame) + action;
      deferred.resolve(
        EpticaHelper.submitWithAttachmentsAndWaitForLoad(
          $q,
          $i18next,
          $log,
          toastDeprecatedService,
          sessionDeprecatedService,
          frame,
          form.id,
          action
        )
      );
    };
    return EpticaHelper.checkPendingAskForHelpAndSubmit($q, frame, form, submitForm);
  }

  static checkPendingAskForHelpAndSubmit<T>(
    $q: IQService,
    frame: HTMLIFrameElement,
    form: HTMLFormElement,
    submitForm: (deferred: IDeferred<T>) => void,
    askForHelpCancellationComment = ' '
  ): IPromise<T> {
    const deferred = $q.defer<T>();
    // tslint:disable:only-arrow-functions
    const askForHelpCancellationCallback = function () {
      $(form).find('input[name="askForHelpCancellationComment"]').val(askForHelpCancellationComment);
      $(form).find('input[name="cancelPendingAskForHelp"]').val('true');
      submitForm(deferred);
    };
    // tslint:enable

    (frame.contentWindow as any).checkForPendingAskForHelp(
      function presenceCallback() {
        askForHelpCancellationCallback();
      },
      function absenceCallback() {
        submitForm(deferred);
      }
    );

    return deferred.promise;
  }

  static cancelAskForReviewClick(frame: HTMLIFrameElement): void {
    return EpticaHelper.simulateClick(frame, 'askForReview_promptPopupCancelButton');
  }

  static cancelAssistClick(frame: HTMLIFrameElement): void {
    return EpticaHelper.simulateClickOnSelection(frame, '#createAskForHelp input[name="commonRequest.cancel"]', true, true);
  }

  static acceptApprovalClick(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement
  ): IPromise<EpticaData> {
    return EpticaHelper.simulateClickAndWaitForLoad(
      $q,
      $i18next,
      $log,
      toastDeprecatedService,
      sessionDeprecatedService,
      frame,
      'accept-review'
    );
  }

  static rejectApprovalClick(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement
  ): IPromise<EpticaData> {
    return EpticaHelper._formAnswerCheckPendingAskForHelpAndSubmit(
      $q,
      $i18next,
      $log,
      toastDeprecatedService,
      sessionDeprecatedService,
      frame,
      '/serviceRequest/rejectReview.action'
    );
  }

  static cancelRejectApprovalClick(frame: HTMLIFrameElement): void {
    return EpticaHelper.simulateClick(frame, 'rejectReview_promptPopupCancelButton');
  }

  static requestRejectApprovalClick(frame: HTMLIFrameElement): void {
    return EpticaHelper.simulateClick(frame, 'reject-review');
  }

  static requestAskForReviewClick($q: IQService, frame: HTMLIFrameElement): IPromise<boolean> {
    const deferred = $q.defer<boolean>();
    // click to load the eptica popin
    EpticaHelper.simulateClick(frame, 'askForReview-button');

    setTimeout(() => {
      // check if the popin is loaded
      const element = this.getEpticaElementById(frame, 'askForReview_promptPopup');
      if (!!element) {
        deferred.resolve(true);
      } else {
        deferred.reject();
      }
    }, 1000);
    return deferred.promise;
  }

  static openKnowlegdeClick(frame: HTMLIFrameElement): void {
    return EpticaHelper.simulateClick(frame, 'browse-knowledgebase');
  }

  static insertKnowlegdeClick(frame: HTMLIFrameElement): void {
    return EpticaHelper.simulateClick(frame, 'insert-answer-button');
  }

  static confirmCloseAssists(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement
  ): IPromise<EpticaData> {
    return EpticaHelper.simulateClickAndWaitForLoad(
      $q,
      $i18next,
      $log,
      toastDeprecatedService,
      sessionDeprecatedService,
      frame,
      'askForHelpCancellation_promptPopupValidateButton'
    );
  }

  static askForHelpAnswerClick(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement
  ): IPromise<EpticaData> {
    return EpticaHelper.submitFormAndWailtForLoad(
      $q,
      $i18next,
      $log,
      toastDeprecatedService,
      sessionDeprecatedService,
      frame,
      'answerAskForHelp'
    );
  }

  static focus(frame: HTMLIFrameElement): void {
    frame.contentWindow!.focus();
  }

  static print(frame: HTMLIFrameElement): void {
    if (frame && frame.contentDocument) {
      let printBtn = frame.contentDocument.getElementById('btn_print');
      if (printBtn) {
        let e$ = EpticaHelper.epticaJQuery(frame);
        e$(printBtn).trigger('click');
      }
    }
  }

  static preview(frame: HTMLIFrameElement): void {
    return EpticaHelper.simulateClick(frame, 'formAnswerRequest_commonRequest_preview');
  }

  static previewOutGoing(frame: HTMLIFrameElement): void {
    return EpticaHelper.simulateClick(frame, 'createRequest_commonRequest_preview');
  }

  static callEpticaFn<T>(frame: HTMLIFrameElement, $log: ILogService | undefined, funcName: string, ...args: Array<any>): Option<T> {
    if (frame.contentWindow && frame.contentWindow[funcName]) {
      try {
        return Option.of(frame.contentWindow[funcName].apply(frame.contentWindow, args));
      } catch (e) {
        if (!!$log) {
          $log.debug(e);
        }
      }
    } else {
      if (frame.contentWindow && frame.contentWindow['eptica'] && frame.contentWindow['eptica'][funcName]) {
        try {
          return Option.of(frame.contentWindow['eptica'][funcName].apply(frame.contentWindow['eptica'], args));
        } catch (e) {
          if (!!$log) {
            $log.debug(e);
          }
        }
      }
    }
    return None;
  }

  static getActionMessages(frame: HTMLIFrameElement): Array<string> {
    let actionMessages = new Array<string>();
    $(frame)
      .contents()
      .find('.alertContainer li')
      .each((_: number, elem: Element) => {
        actionMessages.push(elem.textContent || '');
      });
    return actionMessages;
  }

  static getAlertMessages(frame: HTMLIFrameElement): Array<string> {
    let actionMessages = new Array<string>();
    $(frame)
      .contents()
      .find('div.alert li span')
      .each((_: number, elem: Element) => {
        actionMessages.push(elem.textContent || '');
      });
    return actionMessages;
  }

  static getValidationMessages(frame: HTMLIFrameElement, containerId: string): Array<string> {
    let actionMessages = new Array<string>();
    $(frame)
      .contents()
      .find('#' + containerId + ' span.errorMessage')
      .each((_: number, elem: Element) => {
        actionMessages.push(elem.textContent || '');
      });
    return actionMessages;
  }

  static customizeAnswerDisplay(
    frame: HTMLIFrameElement,
    $i18next: i18n,
    sessionDeprecatedService: SessionDeprecatedService,
    direction: ChannelDirection
  ): void {
    EpticaHelper.updateAttachmentButton(frame, $i18next, sessionDeprecatedService, direction);
  }

  static updateAttachmentButton(
    frame: HTMLIFrameElement,
    $i18next: i18n,
    sessionDeprecatedService: SessionDeprecatedService,
    direction: ChannelDirection
  ): void {
    // Deal with Attachment button
    if (!sessionDeprecatedService.hasPermission('CSR_MAIL_INCOMING::contact:add_attachment')) {
      let divAddAttachmentChild: HTMLElement;
      if (direction === ChannelDirection.INCOMING) {
        divAddAttachmentChild = this.getEpticaElementById(frame, 'formAnswerRequest_answerRequest_messageAttachments_files');
      } else {
        divAddAttachmentChild = this.getEpticaElementById(frame, 'createRequest_answerRequest_messageAttachments_files');
      }
      if (divAddAttachmentChild) {
        let divAddAttachment = divAddAttachmentChild.parentNode as HTMLDivElement;
        if (divAddAttachment) {
          divAddAttachment.style.display = 'none';
        }
      }
    } else {
      // for INCOMING, is created in the layer
      if (direction === ChannelDirection.OUTGOING) {
        this.addUploadButton(frame, $i18next);
      }
    }
  }

  static dummyPromise($q: IQService): (s: string) => IPromise<Select2Data[]> {
    return (s: string) => {
      let deferred = $q.defer<Select2Data[]>();
      setTimeout(() => deferred.resolve([{ id: '' + Date.now(), text: 'query was ' + s } as Select2Data]), 500);
      return deferred.promise;
    };
  }

  static getCcAndBccValues(frame: HTMLIFrameElement, direction: ChannelDirection): Array<string> {
    return [...this.getCcOrBccValues(frame, direction, 'cc'), ...this.getCcOrBccValues(frame, direction, 'bcc')].filter(i =>
      isEmailValid(i)
    );
  }

  static getCcOrBccValues(frame: HTMLIFrameElement, direction: ChannelDirection, ccOrBcc: 'cc' | 'bcc'): Array<string> {
    const e$ = EpticaHelper.epticaJQuery(frame);
    const formId: string = EpticaHelper.getCcOrBccFields(direction, ccOrBcc).formId;

    return (e$(`#${formId}`).val() || '').trim().split(';');
  }

  static getCcOrBccFields(direction: ChannelDirection, ccOrBcc: 'cc' | 'bcc'): any {
    let formS2Id: string;
    let formId: string;
    let formName: string;
    let fieldName: string;
    if (direction === ChannelDirection.INCOMING) {
      formName = 'answerMessage_' + ccOrBcc + 'RecipientsForView';
      formId = 'formAnswerRequest_' + formName;
      formS2Id = 's2id_' + formId;
      fieldName = 'answerMessage.' + ccOrBcc + 'RecipientsForView';
    } else {
      formName = 'createMessage_' + ccOrBcc + 'Recipients';
      formId = 'createRequest_' + formName;
      formS2Id = 's2id_' + formId;
      fieldName = 'createMessage.' + ccOrBcc + 'Recipients';
    }
    return { formS2Id: formS2Id, formId: formId, fieldName: fieldName };
  }

  static addCCAutoCompletion(frame: HTMLIFrameElement, direction: ChannelDirection, ccOrBcc: 'cc' | 'bcc'): void {
    const { formS2Id, formId, fieldName } = EpticaHelper.getCcOrBccFields(direction, ccOrBcc);

    // Find the legacy component
    let legacyCC: HTMLElement | null = this.getEpticaElementById(frame, formS2Id);
    let legacyFormCC: HTMLElement = this.getEpticaElementById(frame, formId);

    if (!legacyCC && !!legacyFormCC && !!legacyFormCC.parentElement) {
      // >= eptica 9.7.1
      legacyCC = legacyFormCC.parentElement.querySelector('.select2-container');
    }
    if (legacyCC) {
      let parentCC = legacyCC.parentElement;

      if (parentCC) {
        // Remove the legacy component
        legacyCC.parentNode!.removeChild(legacyCC);
        if (legacyFormCC) {
          legacyFormCC.parentNode!.removeChild(legacyFormCC);
        }

        const configName = ccOrBcc === 'cc' ? 'ccSAYT' : 'bccSAYT';
        let defaultValue = '';
        if (frame.contentWindow && frame.contentWindow['epticaConfig'] && frame.contentWindow['epticaConfig'][configName]) {
          defaultValue = frame.contentWindow['epticaConfig'][configName]['initialRecipients'] || '';
        }
        // Create the select2 place holder
        let newCCPlaceholder: HTMLSpanElement = frame.contentDocument!.createElement('input');
        newCCPlaceholder.setAttribute('type', 'text');
        newCCPlaceholder.setAttribute('name', fieldName);
        newCCPlaceholder.setAttribute('value', '');
        newCCPlaceholder.setAttribute('tabindex', '21');
        newCCPlaceholder.setAttribute('id', formId);
        parentCC.appendChild(newCCPlaceholder);

        // Call select2 method
        EpticaHelper.initSelect2(
          frame,
          fieldName,
          defaultValue,
          SearchService.instance.acMailUsersAndContacts.bind(SearchService.instance)
        );
      }
    }
  }

  static initSelect2(frame: HTMLIFrameElement, elementName: string, defaultValue: string, callback: (text: string) => IPromise<any>): void {
    let e$ = EpticaHelper.epticaJQuery(frame);
    let wInput = e$(EpticaHelper.getEpticaElementBySelector(frame, "input[name='" + elementName + "']"));

    let options = {
      tags: [],
      multiple: true,
      minimumInputLength: 2,
      separator: ';',
      tokenSeparators: [',', ';'],
      formatSearching: null,
      ajax: {
        data: identity,
        cache: true,
        transport: (params: any) => {
          callback(params.data)
            .then(data => params.success({ results: data.data }))
            .catch(() => params.success({ results: [] }));
        },
        results: identity,
      },
      createSearchChoice: (term, data) => {
        if (term && isEmailValid(term)) {
          if (!data.some(item => item.id === term)) {
            return { id: term, text: term };
          }
        }
        return undefined;
      },
    };

    // Select2 clear the input when a click is made ouside the field
    // We want to avoid that
    let currentValue = '';
    let currentWith: any = 10;
    let setinput = () => wInput.select2('container').find('input').width(currentWith).val(currentValue);
    let getinput = (e: any) => {
      currentValue = e.target.value;
      currentWith = e$(e.target).width();
    };
    let clearsearch = () =>
      wInput
        .select2('container')
        .find('input')
        .width(10)
        .val((currentValue = ''));
    wInput
      .select2(options)
      .on('change', clearsearch)
      .on('select2-blur', setinput)
      .on('select2-flocus', setinput)
      .on('select2-close', setinput);
    wInput.select2('container').find('input').keydown(getinput).keyup(getinput);

    if (defaultValue) {
      let mails: string[] = defaultValue.split(';');
      // remove duplicates
      mails = mails.filter((val: string, index: number, arr: string[]) => arr.indexOf(val) === index);
      const results: Select2Data[] = [];
      mails.forEach((address: string) => results.push({ text: address, id: address }));
      wInput.select2('data', results);
      wInput.val(mails.join(';'));
      wInput.trigger('change');
      wInput.on('select2-removed', EpticaHelper.onCopyFieldsValueRemoveCallback(mails, wInput));
      wInput.on('select2-removing', EpticaHelper.onCopyFieldsValueRemoveCallback(mails, wInput));
    }
  }

  static onCopyFieldsValueRemoveCallback(values: string[], slect2ElementReference: any): (e) => void {
    return e => {
      let currentValues: string = slect2ElementReference.val();
      if (values.indexOf(e.val) > -1 && currentValues.indexOf(e.val) > -1) {
        currentValues = currentValues.replace(e.val, '');
        // remove duplicate comas
        currentValues = currentValues.replace(';;', ';');
        // remove start coma
        if (currentValues.indexOf(';') === 0) {
          currentValues = currentValues.substr(1);
        }
        // remove trailing coma
        if (currentValues.length > 0 && currentValues.indexOf(';') === currentValues.length - 1) {
          currentValues = currentValues.substr(0, currentValues.length - 2);
        }
        slect2ElementReference.val(currentValues);
      }
    };
  }

  /**
   * Helper methods to get the value of one of the Frame's recipient fields (to/cc/bcc) as an array of strings
   * @param frame
   */
  static getCcRecipients(frame: HTMLIFrameElement): string[] {
    return this.getRecipientListBySelector(frame, 'formAnswerRequest_answerMessage_ccRecipientsForView');
  }
  static getBccRecipients(frame: HTMLIFrameElement): string[] {
    return this.getRecipientListBySelector(frame, 'formAnswerRequest_answerMessage_bccRecipientsForView');
  }
  static getToRecipients(frame: HTMLIFrameElement): string[] {
    return this.getRecipientListBySelector(frame, 'formAnswerRequest_answerMessage_toRecipientsForView');
  }
  static getOutgoingCcRecipients(frame: HTMLIFrameElement): string[] {
    return this.getRecipientListBySelector(frame, 'createRequest_createMessage_ccRecipients');
  }
  static getOutgoingBccRecipients(frame: HTMLIFrameElement): string[] {
    return this.getRecipientListBySelector(frame, 'createRequest_createMessage_bccRecipients');
  }
  static getOutgoingToRecipients(frame: HTMLIFrameElement): string[] {
    return this.getRecipientListBySelector(frame, 'createRequest_createMessage_toRecipients');
  }
  private static getRecipientListBySelector(frame: HTMLIFrameElement, elementId: string) {
    const input: HTMLInputElement = this.getEpticaElementById(frame, elementId) as HTMLInputElement;
    let emailAddresses: string[] = [];
    if (input) {
      emailAddresses = input.value.match(EMAIL_LIST_REGEXP) || [];
      // we re-set the input value with the "cleaned" emails to make sure there are no discrepancies between what we return and the actual field
      input.value = emailAddresses.join(';');
    }
    return emailAddresses;
  }

  /**
   * Helper methods to set the value of one of the Frame's recipient fields (to/cc/bcc) from an array of strings
   * @param frame
   */
  static setCcRecipients(frame: HTMLIFrameElement, customers: string[]) {
    this.changeRequestCustomersBySelector(frame, customers, 'formAnswerRequest_answerMessage_ccRecipientsForView');
  }
  static setBccRecipients(frame: HTMLIFrameElement, customers: string[]) {
    this.changeRequestCustomersBySelector(frame, customers, 'formAnswerRequest_answerMessage_bccRecipientsForView');
  }
  static setToRecipients(frame: HTMLIFrameElement, customers: string[]) {
    this.changeRequestCustomersBySelector(frame, customers, 'formAnswerRequest_answerMessage_toRecipientsForView');
  }
  static setOutgoingCcRecipients(frame: HTMLIFrameElement, customers: string[]) {
    this.changeRequestCustomersBySelector(frame, customers, 'createRequest_createMessage_ccRecipients');
  }
  static setOutgoingBccRecipients(frame: HTMLIFrameElement, customers: string[]) {
    this.changeRequestCustomersBySelector(frame, customers, 'createRequest_createMessage_bccRecipients');
  }
  static setOutgoingToRecipients(frame: HTMLIFrameElement, customers: string[]) {
    this.changeRequestCustomersBySelector(frame, customers, 'createRequest_createMessage_toRecipients');
  }

  private static changeRequestCustomersBySelector(frame: HTMLIFrameElement, customers: string[], elementId: string) {
    let input: HTMLInputElement = this.getEpticaElementById(frame, elementId) as HTMLInputElement;
    if (input) input.value = customers.join(';');
  }

  static addUploadButton(frame: HTMLIFrameElement, $i18next: i18n) {
    let label = EpticaHelper.getEpticaElementById(frame, 'formAnswerRequest_answerRequest_messageAttachments_files');
    if (label && label.parentNode) {
      let buttonAlreadyExists = EpticaHelper.getEpticaElementBySelector(frame, 'button.browse-button.addedByContact') != null;
      if (!buttonAlreadyExists) {
        // Create the button
        let spanWrapper: HTMLSpanElement = frame.contentDocument!.createElement('span');
        spanWrapper.setAttribute('class', 'nodisplay');
        spanWrapper.textContent = $i18next.t('agent:contact.mailactions.buttonAdd');

        let button: HTMLButtonElement = frame.contentDocument!.createElement('button');
        button.textContent = $i18next.t('agent:contact.mailactions.buttonAdd');
        button.setAttribute('class', 'browse-button addedByContact');
        button.setAttribute('id', 'browseButton');
        button.onclick = () => {
          EpticaHelper.simulateClickOnSelection(frame, '#answerRequest.messageAttachments.files');
          return false;
        };
        button.appendChild(spanWrapper);

        // insert the button after the label
        label.parentNode.insertBefore(button, label.nextSibling);
      }
    }

    // Sets the width of the textarea to 100%
    label = EpticaHelper.getEpticaElementById(frame, 'formAnswerRequest_commonRequest_message');
    if (label && label.parentElement) {
      label.parentElement.className += ' fullWidth';
    }

    // FOR OUTGOING
    label = EpticaHelper.getEpticaElementById(frame, 'createRequest_answerRequest_messageAttachments_files');
    if (label && label.parentNode) {
      let buttonAlreadyExists = EpticaHelper.getEpticaElementBySelector(frame, 'button.browse-button.addedByContact') != null;
      if (!buttonAlreadyExists) {
        // Create the button
        let button: HTMLButtonElement = frame.contentDocument!.createElement('button');
        button.setAttribute('class', 'browse-button addedByContact addFile');
        button.textContent = $i18next.t('agent:contact.mailactions.buttonAdd');
        button.onclick = () => {
          EpticaHelper.simulateClickOnSelection(frame, 'button.fileBrowseCreate');
          return false;
        };

        // insert the button after the label
        label.parentNode.insertBefore(button, label.nextSibling);
      }
    }
  }
  // hide the recipient zone
  static hideRecipient(frame: HTMLIFrameElement): void {
    let labelRecipient = EpticaHelper.getEpticaElementById(frame, 'createRequest_commonRequest_selectCustomerRecipientInPopup');
    if (!!labelRecipient && !!labelRecipient.parentElement) {
      labelRecipient.parentElement.hidden = true;
    }
  }

  static changeRequestCustomer(frame: HTMLIFrameElement, newIasCustomer: EpticaEnterpriseAgentExposed): void {
    // change the recipient address on Eptica
    EpticaHelper.callEpticaFn(frame, undefined, 'changeRequestCustomer', newIasCustomer.iasId, '');

    // change the visible mail address
    let emailInput = EpticaHelper.getEpticaElementById(frame, 'formAnswerRequest_answerMessage_toRecipientsForView');
    if (!!emailInput) {
      emailInput.setAttribute('value', newIasCustomer.uri || '');
    }
  }

  // hide or show the recipient address
  static toggleRecipientAddress(frame: HTMLIFrameElement, show: boolean): void {
    let container = EpticaHelper.getEpticaElementById(frame, 'formAnswerRequest_commonRequest_clientRecipient');

    if (show) {
      this.showElement(frame, '#formAnswerRequest_commonRequest_clientRecipient');
      if (!!container.parentElement) {
        container.parentElement.removeAttribute('hiddenbycontact');
      }
    } else {
      this.hideElement(frame, '#formAnswerRequest_commonRequest_clientRecipient');
      this.hideElement(frame, '#formAnswerRequest_answerRequest_message_toRecipients');
      if (!!container.parentElement) {
        container.parentElement.setAttribute('hiddenbycontact', 'true');
      }
    }
  }

  static addRecipientButton(
    responseId: string,
    frame: HTMLIFrameElement,
    requestId: number,
    $i18next: i18n,
    contactFacade: BookingFacade,
    profileService: ProfileService,
    parentRef: any
  ) {
    if (!!frame && !!frame.contentWindow) {
      const canChangeRecipient = profileService.hasPermission('CSR_MAIL_INCOMING::contact:change_mail_address');
      const labelRecipient = EpticaHelper.getEpticaElementById(frame, 'formAnswerRequest_commonRequest_clientRecipient');
      if (labelRecipient) {
        labelRecipient.textContent = $i18next.t('agent:contact.mailactions.labelRecipient');
        labelRecipient.removeAttribute('hiddenbycontact');
      }

      const emailIdEl = 'formAnswerRequest_answerRequest_message_toRecipients_email';
      const buttonAlreadyExists = EpticaHelper.getEpticaElementById(frame, emailIdEl) != null;
      if (!buttonAlreadyExists) {
        // get the  customer of the request
        const emailFromIas = EpticaHelper.callEpticaFn<any>(frame, undefined, 'getRequestCustomer', requestId)
          .flatMap(customer =>
            // get the customer's email address
            EpticaHelper.callEpticaFn<string>(frame, undefined, 'getCustomerFieldValue', customer, 'EMail')
          )
          .map(uri => uri.trim().toLowerCase())
          .getOrElse('');
        // adding element depends on customer addresses
        contactFacade
          .getCustomerAddressesById(responseId)
          .pipe(
            first(),
            map(addresses => addresses.filter(({ channel, externalId }) => channel.type === ChannelType.MAIL && !!externalId)),
            ng1Destroy(parentRef)
          )
          .subscribe(addresses => {
            const inputRecipientEptica = EpticaHelper.getEpticaElementById(frame, 'formAnswerRequest_answerRequest_message_toRecipients');
            if (inputRecipientEptica && inputRecipientEptica.parentElement && inputRecipientEptica.parentNode) {
              inputRecipientEptica.parentElement.setAttribute('class', 'recipient-container addedByContact');
              inputRecipientEptica.parentElement.removeAttribute('hiddenbycontact');
              // remove previous element
              const oldEmailIdEl = frame.contentDocument!.getElementById(emailIdEl);
              if (oldEmailIdEl && oldEmailIdEl.parentNode) {
                oldEmailIdEl.parentNode.removeChild(oldEmailIdEl);
              }
              if (canChangeRecipient && addresses.length > 1) {
                // create the select with the email address
                const selectList: HTMLSelectElement = frame.contentDocument!.createElement('select');
                selectList.setAttribute('id', emailIdEl);
                selectList.setAttribute('class', 'addedByContact');
                // create and append the address options
                for (let i = 0; i < addresses.length; i++) {
                  const option = frame.contentDocument!.createElement('option');
                  option.value = addresses[i].id;
                  option.text = addresses[i].uri;
                  selectList.add(option, null);
                }
                // selec current address
                const selectedAddress = addresses.find(({ uri }) => uri && uri.trim().toLowerCase() === emailFromIas);
                if (selectedAddress) {
                  selectList.value = selectedAddress.id;
                }
                // on change value
                selectList.addEventListener(
                  'change',
                  () => {
                    const found = addresses.find(({ uri }) => uri && uri.trim().toLowerCase() === emailFromIas);
                    if (found) {
                      EpticaHelper.callEpticaFn(frame, undefined, 'changeRequestCustomer', found.externalId, '');
                    }
                  },
                  false
                );
                // append selectList in dom
                inputRecipientEptica.parentNode.insertBefore(selectList, inputRecipientEptica.nextSibling);
              } else {
                // create the inputText with the email address
                const inputText: HTMLInputElement = frame.contentDocument!.createElement('input');
                inputText.setAttribute('type', 'text');
                inputText.setAttribute('id', emailIdEl);
                inputText.setAttribute('class', 'addedByContact');
                inputText.setAttribute('readonly', 'readonly');
                inputText.setAttribute('value', emailFromIas);
                // append inputText in dom
                inputRecipientEptica.parentNode.insertBefore(inputText, inputRecipientEptica.nextSibling);
                // change customer
                const [firstAddress] = addresses;
                if (firstAddress && firstAddress.uri && firstAddress.uri.trim().toLowerCase() !== emailFromIas) {
                  // change the recipient address on Eptica
                  EpticaHelper.callEpticaFn(frame, undefined, 'changeRequestCustomer', firstAddress.externalId, '');
                }
              }
            }
          });
      }
    }
  }

  static clearDropZone(frame: HTMLIFrameElement) {
    if (!!frame) {
      let myFrame = window.parent.frames[frame.name];
      if (!!myFrame) {
        // let the dropzone to be loaded
        // remove the files if already added
        myFrame.Dropzone.forElement(EpticaDropZoneElements.ANSWER_REQUEST).removeAllFiles();
      }
    }
  }

  static removeAttachmentFile(frame: HTMLIFrameElement, $log: ILogService, file: IEpticaExctractAttachment, element: string) {
    if (!!frame) {
      try {
        const myFrame = window.parent.frames[frame.name];
        if (!!myFrame && !!file && !!file.file) {
          // let the dropzone to be loaded
          // remove the file if already added
          myFrame.Dropzone.forElement(element).removeFile(file.file);
          if (
            file.action === IEpticaExtractAttachmentAction.ADDED_BY_DRAFT_LOADING ||
            file.file.action === IEpticaExtractAttachmentAction.ADDED_BY_DRAFT_LOADING
          ) {
            if (!!file.file._removeLink) {
              file.file._removeLink.click();
            } else {
              if (!!file.file.file._removeLink) {
                file.file.file._removeLink.click();
              }
            }
          } else {
            myFrame.Dropzone.forElement(element).removeFile(file.file);
          }
        }
      } catch (error) {
        $log.error(`error in removeAttachmentFile '${element}'`, error);
      }
    }
  }

  // Add automatically attachement files to the eptica's DropZone
  static addUploadFiles(frame: HTMLIFrameElement, files: File[], isOutgoing: boolean = false): void {
    if (!!frame && files) {
      const myFrame = window.parent.frames[frame.name];
      if (!!myFrame) {
        // let the dropzone to be loaded
        // then upload the files
        let elementName: string;
        if (!isOutgoing) {
          elementName = EpticaDropZoneElements.ANSWER_REQUEST;
        } else {
          elementName = EpticaDropZoneElements.CREATE_REQUEST;
        }
        myFrame.Dropzone.forElement(elementName).handleFiles(files);
      }
    }
  }

  // add the dropzone events (add/delete)
  static addPJEvents(
    frame: HTMLIFrameElement,
    callback: any,
    $log: ILogService,
    toastService: ToastDeprecatedService,
    _i18n: i18n,
    element: string
  ): void {
    try {
      if (!!frame && !!callback) {
        const myFrame = window.parent.frames[frame.name];
        if (!!myFrame) {
          // let the dropzone to be loaded
          const drpZone = EpticaHelper.epticaJQuery(frame)(element);
          if (drpZone.length) {
            myFrame.Dropzone.forElement(element)
              //  when a file is added, call the callback just to be able to remove the File
              .on('addedfile_contact', file => {
                if (!!file && !!file.name && file.name !== 'nofiles') {
                  callback({ title: file.name, source: undefined, file: file, action: IEpticaExtractAttachmentAction.ADDED });
                }
              })
              // then when a file is removed, call the callback
              .on('removedfile_contact', file => {
                callback({ title: file.title || file.name, source: undefined, file: file, action: IEpticaExtractAttachmentAction.REMOVED });
              })
              .on('error_contact', file => {
                const serverResponse = file.serverResponse;
                if (file.status === 'error') {
                  toastService.alert(
                    _i18n.t('commons:upload.default_error_message', { filename: file.title || file.name }),
                    0,
                    !!serverResponse ? serverResponse.replaceAll(':', '-') : _i18n.t('commons:upload.default_error_title')
                  );
                } else {
                  toastService.notify(
                    !!serverResponse
                      ? serverResponse
                      : _i18n.t('commons:upload.default_error_message', { filename: file.title || file.name }),
                    5000
                  );
                }
                callback({ title: file.title || file.name, source: undefined, file: file, action: IEpticaExtractAttachmentAction.REMOVED });
              })
              .on('canceled_contact', file => {
                toastService.notify(_i18n.t('commons:upload.default_error_message', { filename: file.title || file.name }), 5000);
                callback({ title: file.title || file.name, source: undefined, file: file, action: IEpticaExtractAttachmentAction.REMOVED });
              })
              .on('maxfilesexceeded', _wrapper => {
                toastService.alert(
                  _i18n.t('commons:upload.max_files_exceeded_message', { filename: _wrapper.file.title || _wrapper.file.name }),
                  0,
                  _wrapper.all.dictMaxFilesExceeded.replaceAll(':', '-')
                );
                callback({
                  title: _wrapper.file.title || _wrapper.file.name,
                  source: undefined,
                  file: _wrapper.file,
                  action: IEpticaExtractAttachmentAction.REMOVED,
                });
              })
              .on('maxfilestotalsizeexceeded', _wrapper => {
                toastService.alert(
                  _i18n.t('commons:upload.max_files_exceeded_message', { filename: _wrapper.file.title || _wrapper.file.name }),
                  0,
                  _wrapper.all.dictMaxFilesTotalSizeExceeded.replaceAll(':', '-')
                );
                callback({
                  title: _wrapper.file.title || _wrapper.file.name,
                  source: undefined,
                  file: _wrapper.file,
                  action: IEpticaExtractAttachmentAction.REMOVED,
                });
              })
              .on('maxfilesreached', __this => {
                const maxFile = (__this.eeaFileSizeData && __this.eeaFileSizeData.maxFileNumber) || '';
                toastService.notify(_i18n.t('commons:upload.max_files_reached_title', { maxfilenumber: maxFile }), 5000);
              });
          }
        }
      }
    } catch (error) {
      $log.error(`error in addPJEvents for '${element}'`, error);
    }
  }

  static removePJEventsForElement(frame: HTMLIFrameElement, $log: ILogService, element: string): void {
    if (!!frame) {
      const myFrame = window.parent.frames[frame.name];
      if (!!myFrame) {
        // let the dropzone to be loaded and disconnect events
        try {
          const drpZone = EpticaHelper.epticaJQuery(frame)(element);
          if (drpZone.length) {
            myFrame.Dropzone.forElement(element)
              .off('addedfile_contact')
              .off('removedfile_contact')
              .off('error_contact')
              .off('canceled_contact')
              .off('maxfilesexceeded')
              .off('maxfilesreached');
          }
        } catch (error) {
          $log.error(`error in removePJEvents '${element}'`, error);
        }
      }
    }
  }

  static removePJEvents(frame: HTMLIFrameElement, $log: ILogService): void {
    this.removePJEventsForElement(frame, $log, EpticaDropZoneElements.ANSWER_REQUEST);
  }

  static copyLastHistoryToEditor(
    frame: HTMLIFrameElement,
    historicFrom: string,
    historicTo: string,
    historicCc: string,
    historicSubject: string,
    historicDateTime: string,
    historicContentHtml: string,
    $i18next: i18n
  ): HTMLElement {
    const from: string = `<strong>${$i18next.t('commons:history.mail.from')} : </strong>`;
    const to: string = `<strong>${$i18next.t('commons:history.mail.to')} : </strong>`;
    const cc: string = `<strong>${$i18next.t('commons:history.mail.cc')} : </strong>`;
    const subject: string = `<strong>${$i18next.t('commons:history.mail.subject')} : </strong>`;
    const dateTime: string = `<strong>${$i18next.t('commons:history.mail.sent')} : </strong>`;
    const separator: string = '<br /><hr />';
    const lineBreak: string = '<br />';

    if (!isEmpty(historicCc)) {
      historicCc = cc.concat(historicCc, lineBreak);
    }

    const contentHtml: string = separator.concat(
      from,
      historicFrom,
      lineBreak,
      dateTime,
      historicDateTime,
      lineBreak,
      to,
      historicTo,
      lineBreak,
      historicCc,
      subject,
      historicSubject,
      lineBreak,
      lineBreak,
      historicContentHtml
    );

    if (frame && contentHtml) {
      EpticaHelper.setHtmlEditorText(frame.name, contentHtml);
    }

    return frame;
  }

  // Add txt to the eptica's editor
  static addTxtToEditor(frame: HTMLIFrameElement, txt: string, $i18next: i18n): HTMLElement {
    if (frame && txt) {
      // add a specific label
      let lblSuggestedAnswer = frame.contentDocument!.getElementById('formAnswerRequest_commonRequest_message_suggested');
      if (!lblSuggestedAnswer) {
        lblSuggestedAnswer = frame.contentDocument!.createElement('span');
        lblSuggestedAnswer.textContent = $i18next.t('agent:contact.mailactions.buttonClearDraftAdd_label');
        lblSuggestedAnswer.setAttribute('id', 'formAnswerRequest_commonRequest_message_suggested');
        let parentLbl = frame.contentDocument!.getElementById('formAnswerRequest_commonRequest_message');
        if (parentLbl) {
          parentLbl.appendChild(lblSuggestedAnswer);
        }
      }

      EpticaHelper.setHtmlEditorText(frame.name, txt);
    }
    return frame;
  }

  static setHtmlEditorText(frameName: string, txt: string) {
    // clear the current editor's reponse
    let myFrame = window.parent.frames[frameName];
    // get eptica editor
    if (myFrame) {
      let oEditor = myFrame.getHtmlEditor(myFrame);
      if (oEditor) {
        // clear the eptica text area
        oEditor.setData(txt);
      }
    }
  }

  static addHtmlEditorText(frameName: string, txt: string) {
    // clear the current editor's reponse
    let myFrame = window.parent.frames[frameName];
    // get eptica editor
    if (myFrame) {
      let oEditor = myFrame.getHtmlEditor(myFrame);
      if (oEditor) {
        // add the text to the eptica text area without clearing it
        oEditor.insertHtml(txt);
      }
    }
  }

  static createHideButton(frame: HTMLIFrameElement, texta11y: string, callBackAction: (event) => void): HTMLElement {
    let myButton: HTMLButtonElement = frame.contentDocument!.createElement('button');
    myButton.setAttribute('class', 'hide-field');
    myButton.setAttribute('role', 'button');
    myButton.setAttribute('title', texta11y);

    let mySpan: HTMLSpanElement = frame.contentDocument!.createElement('span');
    mySpan.setAttribute('class', 'nodisplay');
    mySpan.textContent = texta11y;
    myButton.onclick = callBackAction;
    myButton.appendChild(mySpan);

    return myButton;
  }

  // show the element matching the selector
  static showElement(frame: HTMLIFrameElement, selectorName: string): void {
    let originalEpticaElement: HTMLElement = EpticaHelper.getEpticaElementBySelector(frame, selectorName);
    let e$ = EpticaHelper.epticaJQuery(frame);
    if (!!originalEpticaElement) {
      this.toggleAttr(originalEpticaElement, 'hiddenbycontact', false);
      e$(selectorName).css('display', 'unset');
      if (originalEpticaElement.classList.contains('hidden')) {
        e$(selectorName).toggleClass('hidden');
      }
      this.toggleAttr(originalEpticaElement, 'hidden', false);
    }
  }

  // hide the element matching the selector
  static hideElement(frame: HTMLIFrameElement, selectorName: string): void {
    let originalEpticaElement: HTMLElement = EpticaHelper.getEpticaElementBySelector(frame, selectorName);
    if (!!originalEpticaElement) {
      this.toggleAttr(originalEpticaElement, 'hiddenbycontact', true);
    }
  }

  static hideAttachmentInAnswerZone(frame: HTMLIFrameElement) {
    const originalEpticaLabelElement = EpticaHelper.getEpticaElementBySelector(
      frame,
      '#formAnswerRequest_answerRequest_messageAttachments_files'
    );
    if (originalEpticaLabelElement && originalEpticaLabelElement.parentElement) {
      originalEpticaLabelElement.parentElement.setAttribute('hiddenbycontact', 'true');
    }
  }

  // actions on the eptica frame when button is clicked
  static clearDraftAction(frame: HTMLIFrameElement) {
    // clear the editor
    EpticaHelper.setHtmlEditorText(frame.name, '');

    // remove the previous files
    EpticaHelper.clearDropZone(frame);

    // remove original eptica label
    let parentLbl = frame.contentDocument!.getElementById('formAnswerRequest_commonRequest_message');
    let lblToRemove = frame.contentDocument!.getElementById('formAnswerRequest_commonRequest_message_suggested');
    if (!!parentLbl && !!lblToRemove) {
      parentLbl.removeChild(lblToRemove);
    }
  }

  static getReplyAllAddresses(frame: HTMLIFrameElement, blackListMailAddress: string): any {
    return this.callEpticaFn(frame, undefined, 'getAllRecipients', blackListMailAddress).getOrElse({});
  }

  static addLayerButtons(
    $i18next: any,
    sessionDeprecated: SessionDeprecatedService,
    frame: HTMLIFrameElement,
    requestId: string,
    buttonList: EpticaButtonActionTypes[]
  ): void {
    if (!!buttonList) {
      // if unitary outgoing mail : no requestId
      if (requestId) {
        buttonList.forEach(buttonconf => {
          // do not forget children
          if (buttonconf.children.length > 0) {
            this.addLayerButtons($i18next, sessionDeprecated, frame, requestId, buttonconf.children);
          }
          switch (buttonconf.id) {
            case EpticaButtonTypes.PJ: {
              if (sessionDeprecated.hasPermission('CSR_MAIL_INCOMING::contact:add_attachment')) {
                // Hide the existing element
                let originalEpticaElement: HTMLElement = EpticaHelper.getEpticaElementById(
                  frame,
                  'formAnswerRequest_answerRequest_messageAttachments_files'
                ) as HTMLElement;
                if (!!originalEpticaElement && !!originalEpticaElement.parentElement) {
                  originalEpticaElement.parentElement.hidden = true;
                }
              }
              break;
            }
            default:
          }
        });
      }
    }
  }

  static setContactStyleToField(frame: HTMLIFrameElement, fieldId: string): void {
    let epticaElement: HTMLInputElement = this.getEpticaElementById(frame, fieldId) as HTMLInputElement;
    if (!!epticaElement && !!epticaElement.parentElement) {
      epticaElement.parentElement.classList.add('field-container', 'addedByContact');
    }
  }

  static hideFieldInputContainerParent(frame: HTMLIFrameElement, fieldId: string): void {
    let epticaElement: HTMLInputElement = this.getEpticaElementById(frame, fieldId) as HTMLInputElement;
    if (!!epticaElement) {
      $(epticaElement).closest('.input_container').hide();
    }
  }

  static moveSubjectInHeader(frame: HTMLIFrameElement): Option<string> {
    let ret: string = '';
    let epticaSubjectTextElement: HTMLInputElement = this.getEpticaElementById(
      frame,
      'formAnswerRequest_answerMessage_subject'
    ) as HTMLInputElement;

    if (!!epticaSubjectTextElement) {
      let epticaSubjectElement: HTMLElement = epticaSubjectTextElement as HTMLElement;
      if (!!epticaSubjectElement && !!epticaSubjectElement.parentElement && !!epticaSubjectElement.parentElement.parentElement) {
        epticaSubjectElement.parentElement.parentElement.hidden = false;
        epticaSubjectElement.parentElement.classList.add('adjustMailSubjectBlock');
        epticaSubjectElement.parentElement.parentElement.classList.add('changeSubject');
        epticaSubjectElement.removeAttribute('readonly');
        ret = epticaSubjectTextElement.defaultValue;
        // hide the main panel
        let mainElemH3: HTMLElement = this.getEpticaElementBySelector(frame, '#request-answer h3') as HTMLElement;
        if (!!mainElemH3) {
          mainElemH3.hidden = true;
        }
        let mainElem: HTMLElement = this.getEpticaElementById(frame, 'request-actions') as HTMLElement;
        if (!!mainElem) {
          mainElem.setAttribute('class', 'quick-win');
        }
      }
    }
    return Option.of(ret);
  }

  static hide(frame: HTMLIFrameElement, epticaElementId: string): boolean {
    let element: HTMLElement = this.getEpticaElementById(frame, epticaElementId) as HTMLElement;
    if (element) {
      element.style.display = 'none';
      return true;
    }
    return false;
  }

  static isEpticaElementHasStyle(frame: HTMLIFrameElement, elementId: string, styleName: string, styleValue: string): boolean {
    let element: HTMLElement = this.getEpticaElementById(frame, elementId) as HTMLElement;
    if (element && element.style) {
      return element.style[styleName] === styleValue;
    }
    return false;
  }

  /**
   * Display eptica message with toats
   */
  static displayActionMessages(toastDeprecatedServiceService: ToastDeprecatedService, frame: HTMLIFrameElement): void {
    // Display alert messages
    EpticaHelper.getAlertMessages(frame).forEach(value => {
      toastDeprecatedServiceService.alert(value);
    });

    // Display Info messages
    EpticaHelper.getActionMessages(frame).forEach(value => {
      toastDeprecatedServiceService.add(ToastType.NOTIFICATION, value, ToastClass.MAIL, DEFAULT_TOAST_DURATION);
    });
  }

  static displayValidationMessages(
    toastDeprecatedServiceService: ToastDeprecatedService,
    frame: HTMLIFrameElement,
    containerId: string
  ): void {
    EpticaHelper.getValidationMessages(frame, containerId).forEach(value => {
      toastDeprecatedServiceService.alert(value);
    });
  }

  /**
   * Delay as a promise
   */
  static delay<T>($q: IQService, $timeout: ITimeoutService, duration: number, promiseReturn?: T): IPromise<T> {
    let deferred = $q.defer<T>();
    $timeout(() => deferred.resolve(promiseReturn), duration);
    return deferred.promise;
  }

  static addPrintAndPreviewButtons($i18next, sessionDeprecatedService: SessionDeprecatedService, frame: HTMLIFrameElement): void {
    EpticaHelper.addPrintAndPreviewButtonsTo($i18next, sessionDeprecatedService, frame, '#request-answer h3');
  }

  static addPrintButtonForAssists($i18next, sessionDeprecatedService: SessionDeprecatedService, frame: HTMLIFrameElement): void {
    const panelTitle = $i18next.t('agent:contact.mailactions.answerRequestForAssistsPanelTitle');
    EpticaHelper.addPrintAndPreviewButtonsTo(
      $i18next,
      sessionDeprecatedService,
      frame,
      '#request-answer-assistance h3',
      true,
      false,
      panelTitle
    );
  }

  static addPrintAndPreviewButtonsForOutgoing(
    $i18next,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement
  ): void {
    EpticaHelper.addPrintAndPreviewButtonsTo(
      $i18next,
      sessionDeprecatedService,
      frame,
      '#createRequestHeader',
      true,
      true,
      undefined,
      ChannelDirection.OUTGOING
    );
  }

  static addPrintAndPreviewButtonsTo(
    $i18next,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement,
    selector: string,
    printButton: boolean = true,
    previewButton: boolean = true,
    panelTitle?: string,
    direction: ChannelDirection = ChannelDirection.INCOMING
  ): void {
    let canPreview = sessionDeprecatedService.hasPermission('CSR::mail_display:preview');
    let canPrint = sessionDeprecatedService.hasPermission('CSR::mail_display:print');

    let answerPanelTitle = frame.contentDocument!.querySelector(selector) as HTMLElement;
    if (answerPanelTitle) {
      EpticaHelper.removeAddedButtons(answerPanelTitle);

      // Buttons container
      let buttonsContainer = frame.contentDocument!.createElement('DIV');
      buttonsContainer.setAttribute('class', 'header_actions');
      if (panelTitle) {
        answerPanelTitle.innerHTML = panelTitle;
      }
      answerPanelTitle.insertAdjacentElement('afterend', buttonsContainer);

      // Print button
      if (printButton && canPrint) {
        // BUTTON CONTAINER
        let printIconContainer = frame.contentDocument!.createElement('DIV');
        printIconContainer.setAttribute('title', $i18next.t('agent:contact.mailactions.buttonPrint'));
        buttonsContainer.insertAdjacentElement('afterbegin', printIconContainer);

        // BUTTON
        let printIcon = frame.contentDocument!.createElement('BUTTON');
        printIcon.setAttribute('class', 'addedByContact print');
        printIcon.setAttribute('role', 'button');
        printIcon.setAttribute('onclick', 'javascript:window.print()');
        printIconContainer.insertAdjacentElement('afterbegin', printIcon);

        // NODISPLAY CONTEXT LABEL FOR BUTTON
        let printIconLabel = frame.contentDocument!.createElement('SPAN');
        printIconLabel.setAttribute('class', 'nodisplay');
        printIconLabel.textContent = $i18next.t('agent:contact.mailactions.buttonPrint');
        printIcon.insertAdjacentElement('afterbegin', printIconLabel);
      }

      // Preview button
      if (previewButton && canPreview) {
        // BUTTON CONTAINER
        let previewIconContainer = frame.contentDocument!.createElement('DIV');
        previewIconContainer.setAttribute('title', $i18next.t('agent:contact.mailactions.buttonPreview'));
        buttonsContainer.insertAdjacentElement('afterbegin', previewIconContainer);

        let previewIcon = frame.contentDocument!.createElement('BUTTON');
        previewIcon.setAttribute('class', 'addedByContact preview');
        previewIcon.setAttribute('title', $i18next.t('agent:contact.mailactions.buttonPreview'));
        previewIcon.setAttribute('role', 'button');
        previewIcon.onclick = () => {
          let preview = direction === ChannelDirection.INCOMING ? EpticaHelper.preview : EpticaHelper.previewOutGoing;
          preview(frame);
        };
        previewIconContainer.insertAdjacentElement('afterbegin', previewIcon);

        // NODISPLAY CONTEXT LABEL FOR BUTTON
        let previewIconLabel = frame.contentDocument!.createElement('SPAN');
        previewIconLabel.setAttribute('class', 'nodisplay');
        previewIconLabel.textContent = $i18next.t('agent:contact.mailactions.buttonPreview');
        previewIcon.insertAdjacentElement('afterbegin', previewIconLabel);
      }
    }
  }

  static removeAddedButtons(container: HTMLElement): void {
    let buttonElts = container.querySelectorAll('.addedByContact');
    for (let i = 0; i < buttonElts.length; i++) {
      buttonElts[i].parentNode!.removeChild(buttonElts[i]);
    }
  }

  static validateOutgoingSend(frame: HTMLIFrameElement, $log: ILogService): boolean {
    return EpticaHelper.callEpticaFn(frame, $log, 'validateForm').getOrElse(false) as boolean;
  }

  static getContextPath(frame: HTMLIFrameElement): string {
    let pathname = frame.contentWindow!.location.pathname;
    if (pathname[0] === '/') {
      pathname = pathname.substr(1);
    }
    return pathname.substring(0, pathname.indexOf('/'));
  }

  static wrapEpticaHandleFileSubmitMethodAndWait($q: IQService, frame: HTMLIFrameElement): IPromise<EpticaData> {
    let epticaWindow = frame.contentWindow as any;
    if (!isDefined(epticaWindow.oldHandleFileSubmit)) {
      epticaWindow.oldHandleFileSubmit = epticaWindow.handleFileSubmit;
    }

    let defered = $q.defer<EpticaData>();
    epticaWindow.handleFileSubmit = (file, statusMessageClass) => {
      let xhr = file ? (file[0] ? file[0].xhr : null) : null;
      if (!xhr) {
        defered.reject('xhr is null');
      }
      if (xhr.status === 200) {
        epticaWindow.oldHandleFileSubmit(file, statusMessageClass);
        defered.resolve(optToPromise(EpticaHelper.getEpticaData(frame), $q));
      } else {
        epticaWindow.oldHandleFileSubmit(file, statusMessageClass);
        defered.reject('xhr status is ' + xhr.status);
      }
    };
    return defered.promise;
  }

  static sendOutgoingClick(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement
  ): IPromise<EpticaData> {
    return EpticaHelper.simulateClickAndAttachmentsAndLoad(
      $q,
      $i18next,
      $log,
      toastDeprecatedService,
      sessionDeprecatedService,
      frame,
      'createRequest_commonRequest_sendAndClose'
    );
  }

  static sendPostponeOutgoingClick(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement
  ): IPromise<EpticaData> {
    // return EpticaHelper.simulateClickAndWaitForLoad($q, $i18next, toastDeprecatedService, sessionDeprecatedService, frame, 'createRequest_commonRequest_sendAndPostpone');
    return EpticaHelper.simulateClickAndAttachmentsAndLoad(
      $q,
      $i18next,
      $log,
      toastDeprecatedService,
      sessionDeprecatedService,
      frame,
      'createRequest_commonRequest_sendAndPostpone'
    );
  }

  static browseKonwledgeOutgoingClick(frame: HTMLIFrameElement): void {
    EpticaHelper.simulateClick(frame, 'browse-knowledgebase');
  }

  static overrideKnowledgeBehaviour(frame: HTMLIFrameElement) {
    let e$ = EpticaHelper.epticaJQuery(frame);
    e$('#browse-kb-popup').off('click');
    e$('div#browse-kb-popup.modal.fade.in').off('keyup').off('keydown');
  }

  static closeEpticaModal(frame: HTMLIFrameElement, id: string): void {
    let e$ = EpticaHelper.epticaJQuery(frame);
    e$('#' + id).modal('hide');
  }

  static epticaJQuery(frame: HTMLIFrameElement): any {
    return (frame.contentWindow as any).$;
  }

  /**
   * Create a button HTML
   */
  static overrideKnowledgeDialogCancel(container: HTMLElement, onclickAction: () => void): HTMLButtonElement {
    // Create the button
    let button: HTMLButtonElement = container.ownerDocument!.createElement('button');
    button.setAttribute('data-dismiss', 'modal');
    button.setAttribute('arial-label', '[Close]');

    // The eptica button are hidden with css. Add this class to force visible
    // button.classList.add('addedByContact');
    // button.classList.add('close');
    $(button).toggleClass('addedByContact', true);
    // ie fix
    $(button).toggleClass('close', true);

    // Adding the content
    button.innerHTML = '<span aria-hidden="true">×</span>';

    // Sets the onclick action
    button.onclick = () => {
      onclickAction();
    };

    // Add the button to the container
    let cancelButton = container.querySelector('#browse-kb-popup button.close');
    if (cancelButton && cancelButton.parentNode) {
      cancelButton.parentNode.insertBefore(button, cancelButton.nextSibling);
    }

    return button;
  }

  static hideAttachmentsWithZeroSize(frame: HTMLIFrameElement) {
    let elements = this.getEpticaElementBySelectorAll(frame, '.dz-file-preview .dz-size');
    for (let i = 0; i < elements.length; i++) {
      let element = elements.item(i) as HTMLElement;
      let sizeEl = element.querySelector('span strong');
      if (sizeEl && sizeEl.textContent === '0') {
        element.style.visibility = 'hidden';
      }
    }
  }

  static validateOutgoingDraft(frame: HTMLIFrameElement, $log: ILogService): boolean {
    return !(EpticaHelper.callEpticaFn(frame, $log, 'jsSaveDraft').getOrElse(false) === false);
  }

  static cancelOutgoingDraft(frame: HTMLIFrameElement, $log: ILogService): boolean {
    return !(EpticaHelper.callEpticaFn(frame, $log, 'deleteDraftByWorldline').getOrElse(false) === false);
  }

  static cancelOutgoingDraftClick(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement
  ): IPromise<EpticaData> {
    (this.getEpticaElementById(frame, 'createRequest') as HTMLFormElement).action = '/eptica_ias/serviceDraft/deleteDraft.action';
    (this.getEpticaElementById(frame, 'createRequest') as HTMLFormElement).onsubmit = function () {};
    return EpticaHelper.submitFormAndWailtForLoad(
      $q,
      $i18next,
      $log,
      toastDeprecatedService,
      sessionDeprecatedService,
      frame,
      'createRequest'
    );
  }

  static saveOutgoingDraftClick(
    $q: IQService,
    $i18next: i18n,
    $log: ILogService,
    toastDeprecatedService: ToastDeprecatedService,
    sessionDeprecatedService: SessionDeprecatedService,
    frame: HTMLIFrameElement
  ): IPromise<EpticaData> {
    return EpticaHelper.submitFormAndWailtForLoad(
      $q,
      $i18next,
      $log,
      toastDeprecatedService,
      sessionDeprecatedService,
      frame,
      'createRequest'
    );
  }

  static createButton(container: HTMLElement, label: string, onclickAction: () => void, validationStyle: boolean): HTMLInputElement {
    // Create the button
    let button: HTMLInputElement = container.ownerDocument!.createElement('input');
    button.setAttribute('type', 'button');
    button.setAttribute('value', label);

    // Set the style of the button (2 styles for validate and cancel)
    // button.classList.add(validationStyle ? 'button-apply' : 'button-cancel');
    $(button).toggleClass(validationStyle ? 'button-apply' : 'button-cancel', true);
    // ie fix
    // The eptica button are hidden with css. Add this class to force visible
    // button.classList.add('addedByContact');
    $(button).toggleClass('addedByContact', true);

    // Sets the onclick action
    button.onclick = event => {
      event.preventDefault();
      onclickAction();
    };

    container.appendChild(button);

    return button;
  }

  static replaceEpticaButton(
    buttonId: string,
    frame: HTMLIFrameElement,
    action: () => void,
    conditionMethod: (frame) => boolean = () => true
  ): void {
    if (conditionMethod(frame)) {
      let epticaButton = this.getEpticaElementById(frame, buttonId) as HTMLButtonElement;
      if (epticaButton) {
        epticaButton.style.display = 'none';
        let newButton: HTMLInputElement = EpticaHelper.createButton(epticaButton.parentElement!, epticaButton.value, action, true);
        if (epticaButton.id != null && epticaButton.id !== '') {
          newButton.id = REPLACED_BUTTON_ID_PREFIX + epticaButton.id;
        }
      }
    }
  }

  static getEpticaElementById(frame: HTMLIFrameElement, id: string): HTMLElement {
    return frame.contentWindow!.document.getElementById(id) as HTMLElement;
  }

  static getEpticaElementBySelector(frame: HTMLIFrameElement, selector: string): HTMLElement {
    return frame.contentWindow!.document.querySelector(selector) as HTMLElement;
  }

  static getEpticaElementBySelectorAll(frame: HTMLIFrameElement, selector: string): NodeListOf<Element> {
    return frame.contentWindow!.document.querySelectorAll(selector);
  }

  static getFirstExistingEpticaElementBySelector(frame: HTMLIFrameElement, ...selector: string[]): HTMLElement | undefined {
    let elem: HTMLElement | undefined = undefined;
    if (!!frame && !!frame.contentWindow && !!frame.contentWindow.document) {
      for (let i = 0; i < selector.length; i++) {
        elem = frame.contentWindow.document.querySelector(selector[i]) as HTMLElement;
        if (!!elem) {
          return elem;
        }
      }
    }
    return elem;
  }

  static getFirstExistingEpticaSelector(frame: HTMLIFrameElement, ...selector: string[]): string {
    if (!!frame && !!frame.contentWindow && !!frame.contentWindow.document) {
      for (let i = 0; i < selector.length; i++) {
        const elem = frame.contentWindow.document.querySelector(selector[i]) as HTMLElement;
        if (!!elem) {
          return selector[i];
        }
      }
    }
    return '';
  }

  static transformBounceEvent($i18next: i18n, frame: HTMLIFrameElement) {
    if (frame.contentWindow) {
      // Transform event bounce with comment data
      let bounceElement: HTMLCollection = frame.contentWindow.document.getElementsByClassName('request_event_type_bounce_by_agent');
      // loop on elements
      range(0, bounceElement.length)
        .pipe(
          concatMap(i =>
            of(bounceElement.item(i)).pipe(
              // select balise content text
              map(element => element && $(element).find('.comment_content_text')),
              // get content
              map(div => div && $(div).find('p').text()),
              // transform in object
              map(text => text && fromJson(text)),
              // replace content by comment data in object
              tap(json =>
                $(
                  '.request_event_type_bounce_by_agent:eq(' + i + ') .request_history_info_content_user_comment .comment_content_text p',
                  frame.contentWindow!.document
                ).text(json.comment || '')
              ),
              // in forward case
              filter(json => MailComment.isForwardType(json.type)),
              // replace label
              tap(json => {
                if (MailComment.isForwardToSkillType(json.type)) {
                  const text = $i18next.t('commons:history.mail.transfer_by_skill', {
                    agent: json.agent,
                  });
                  $('.request_event_type_bounce_by_agent:eq(' + i + ') .rhi_infos p', frame.contentWindow!.document).text(text);
                  let el = $('.request_event_type_bounce_by_agent:eq(' + i + ') .Request_open', frame.contentWindow!.document);
                  let source: any[] = Option.of(json.skill)
                    .map(s => s.source)
                    .getOrElse([]);
                  let target: any[] = Option.of(json.skill)
                    .map(s => s.target)
                    .getOrElse([]);
                  el.append(this.generateHtmlForwardSkill($i18next, 'agent:contact.workspace.mail.comment.skill_source', source));
                  el.append(this.generateHtmlForwardSkill($i18next, 'agent:contact.workspace.mail.comment.skill_target', target));
                } else {
                  const text = $i18next.t('commons:history.mail.transfer_by_team', {
                    agent: json.agent,
                    team: json.extraData.target,
                  });
                  $('.request_event_type_bounce_by_agent:eq(' + i + ') .rhi_infos p', frame.contentWindow!.document).text(text);
                }
              }),
              catchError(() => EMPTY)
            )
          )
        )
        .subscribe();
    }
  }

  static transfertSetSpecialist(frame: HTMLIFrameElement, email: string): void {
    let textArea = EpticaHelper.getEpticaElementById(frame, 'delegateRequest_externalSpecialist') as HTMLInputElement;
    textArea.value = email;
  }

  static transfertSetComment(frame: HTMLIFrameElement, email: string): void {
    let textArea = EpticaHelper.getEpticaElementById(frame, 'delegateRequest_delegateMessage_textMessage') as HTMLTextAreaElement;
    textArea.value = email;
  }

  static transfertSetKindOfForward(frame: HTMLIFrameElement, kindOfForward: KindOfForward): void {
    const textArea = EpticaHelper.getEpticaElementById(frame, 'delegateRequest_kindOfForward') as HTMLTextAreaElement;
    textArea.value = kindOfForward;
  }

  static transfertSetAgent(frame: HTMLIFrameElement, iasId: string): void {
    const textArea = EpticaHelper.getEpticaElementById(frame, 'delegateRequest_destinationAccount_accountId') as HTMLInputElement;
    textArea.value = iasId;
  }

  static transfertSetAccountType(frame: HTMLIFrameElement, accoundType: number): void {
    const textArea = EpticaHelper.getEpticaElementById(frame, 'delegateRequest_accountType') as HTMLInputElement;
    textArea.value = String(accoundType);
  }

  static assistanceSetComment(frame: HTMLIFrameElement, email: string): void {
    const textArea = EpticaHelper.getEpticaElementById(frame, 'createAskForHelp_assistanceMessage_textMessage') as HTMLTextAreaElement;
    textArea.value = email;
  }

  static assistanceSetSpecialist(frame: HTMLIFrameElement, email: string): void {
    const textArea = EpticaHelper.getEpticaElementById(
      frame,
      'createAskForHelp_askForHelp_currentAskForHelpEvent_toExternalSpecialistEmail'
    ) as HTMLInputElement;
    textArea.value = email;
    const selectMessage = EpticaHelper.getEpticaElementBySelector(
      frame,
      'input[name="messageSelectionRequestHistory"]'
    ) as HTMLInputElement;
    if (!!selectMessage) {
      // check this input to avoid a confirmation popin
      selectMessage.checked = true;
    }
  }

  static assistanceSetAgent(frame: HTMLIFrameElement, iasId: string): void {
    const textArea = EpticaHelper.getEpticaElementById(
      frame,
      'createAskForHelp_askForHelp_currentAskForHelpEvent_toAccountId'
    ) as HTMLInputElement;
    textArea.value = iasId;
  }

  static assistanceSetKindOfForward(frame: HTMLIFrameElement, kindOfForward: KindOfForward): void {
    const textArea = EpticaHelper.getEpticaElementById(frame, 'createAskForHelp_kindOfForward') as HTMLTextAreaElement;
    textArea.value = kindOfForward;
  }

  static assistanceSetAccountType(frame: HTMLIFrameElement, accoundType: number): void {
    const textArea = EpticaHelper.getEpticaElementById(frame, 'createAskForHelp_accountType') as HTMLInputElement;
    textArea.value = String(accoundType);
  }

  static getIncludedMessages(frame: HTMLIFrameElement): string[] {
    const ret: string[] = [];
    const e$ = this.epticaJQuery(frame);

    e$('input[name="messageSelectionRequestHistory"]').each((_: number, elem) => {
      if (elem.checked) {
        ret.push(elem.getAttribute('value') || '');
      }
    });

    return ret;
  }

  /**
   * Observable methods version
   */
  static waitLoadObs(frame: HTMLIFrameElement): Observable<HTMLIFrameElement> {
    return fromEvent(frame, 'load').pipe(
      first(),
      map(() => frame),
      tap(() => EpticaHelper.setContactClass(frame))
    );
  }

  static waitForDataObs(frame: HTMLIFrameElement, delay: number = 250, nbRetry: number = 4): Observable<EpticaData> {
    let i = 0;
    return EpticaHelper.waitLoadObs(frame).pipe(
      switchMap(f =>
        of(EpticaHelper.getEpticaData(f)).pipe(
          map(data => data.get()),
          retryWhen(errors =>
            errors.pipe(
              observableDelay(delay),
              tap(err => {
                if (i++ >= nbRetry) {
                  throw err;
                }
              })
            )
          )
        )
      )
    );
  }

  static selectAllMessageRequestHistory(frame: HTMLIFrameElement) {
    of(EpticaHelper.getEpticaElementBySelector(frame, '.request_history_info_content'))
      .pipe(
        mergeMap((node: HTMLElement) =>
          [].slice.call(node.querySelectorAll('.request_event_with_message, .request_event_without_message.request_event_ask_for_help'))
        ),
        map((node: HTMLElement) => node.querySelector('input[name="messageSelectionRequestHistory"]'))
      )
      .forEach((input: HTMLElement) => $(input).prop('checked', true));
  }

  /**
   * Replace the file upload button (input type=file which if very difficult to style) by a standard one
   * So, the vanilla button is hidden. A click on the new button simulates a click on the vanilla one.
   */
  static uniformUploadButtonForAskForHelp($i18next: i18n, frame: HTMLIFrameElement) {
    // find existing element
    let vanillaButton: HTMLInputElement = this.getEpticaElementById(
      frame,
      'answerAskForHelp_messageAttachments(0)_file'
    ) as HTMLInputElement;
    if (vanillaButton) {
      let container = vanillaButton.parentElement;
      if (container && container.ownerDocument) {
        let e$ = this.epticaJQuery(frame);

        let moveTitles = () => {
          e$('.filename_delete', container).each((_: number, elem: HTMLElement) => {
            let title = elem.getAttribute('title');
            if (title && elem.firstChild && elem.firstChild.nodeType !== Node.TEXT_NODE && container && container.ownerDocument) {
              elem.insertBefore(container.ownerDocument.createTextNode(title), elem.firstChild);
              elem.removeAttribute('title');
            }
          });
        };

        // create the new button
        let button: HTMLButtonElement = container.ownerDocument.createElement('button');
        e$(button).toggleClass('addedByContact', true);
        e$(button).toggleClass('browse-button', true);

        button.onclick = () => {
          let uploadButton: HTMLInputElement = vanillaButton;
          e$(container)
            .find('input[type=file]')
            .each((_, elem) => {
              if ((elem as HTMLInputElement).name > uploadButton.name) {
                uploadButton = elem as HTMLInputElement;
              }
            });
          this.simulateClick(frame, uploadButton);
          return false;
        };

        // tslint:disable:only-arrow-functions
        $(container).on('DOMSubtreeModified propertychange', function () {
          moveTitles();
        });
        // tslint:enable

        let innerSpan: HTMLSpanElement = container.ownerDocument.createElement('span');
        innerSpan.textContent = $i18next.t('agent:contact.mailactions.buttonAdd');
        button.appendChild(innerSpan);

        // Hide the vanilla button and add the new one
        container.insertBefore(button, vanillaButton);
      }
    }
  }

  static changeIncomingChannelLabel($i18next: i18n, frame: HTMLIFrameElement) {
    let incomingChannelLabel = EpticaHelper.getEpticaElementById(frame, 'createRequest_answerRequest_message_incomingChannel');
    if (incomingChannelLabel) {
      incomingChannelLabel.textContent = $i18next.t('agent:contact.mailactions.incomingChannel');
    }
  }

  static printMessageClientWithHtmlFormat(frame: HTMLIFrameElement) {
    if (frame.contentWindow) {
      let history = $('#historyRequestEvents', frame.contentWindow.document);

      history.find('.switchable_content .switchable_content_html').each((_, el: Element) => {
        el.classList.remove('no-print');
        el.classList.add('printOnly');
      });

      history.find('.switchable_content .switchable_content_text').each((_, el: Element) => {
        el.classList.add('no-print');
        el.classList.remove('printOnly');
      });
    }
  }

  static toggleConversationalMode(frame: HTMLIFrameElement): void {
    let historyTab = EpticaHelper.getEpticaElementById(frame, 'historyTab');
    if (historyTab && frame.contentWindow) {
      let icon = $('#request_history_display_type_icon', frame.contentWindow.document);
      let iconPlaceholder = $('#historyIcon', frame.contentWindow.document);
      let history = $('.request_history_info_content', frame.contentWindow.document);

      history.toggleClass('request_history_display_conversational', true);
      icon.toggleClass('request_history_display_conversational', true);
      iconPlaceholder.toggleClass('fa fa-th-list fa-inverse fa-stack-1x', true);

      history.toggleClass('request_history_display_full', false);
      icon.toggleClass('request_history_display_full', false);
      iconPlaceholder.toggleClass('fa fa-comments fa-inverse fa-stack-1x', false);

      $('.request_event_with_message', frame.contentWindow.document).each((_, el) => {
        let parent = $(el);
        let ul = parent.find('.eptica_ul');
        if (ul.css('display') !== 'block') {
          ul.slideToggle();
          let frameId = parent.find('.switchable_content_html').attr('id');
          (frame.contentWindow as any).resizeIframe(frameId);
        }
      });
    }
  }

  static getAttachments(frame: HTMLIFrameElement): IEpticaExctractAttachment[] {
    const attachments: IEpticaExctractAttachment[] = [];

    let frameDoc: Document;
    if (frame instanceof HTMLIFrameElement) {
      frameDoc = (frame as HTMLIFrameElement).contentWindow!.document;
      const elements: HTMLCollection = frameDoc.getElementsByClassName('dz-preview-link');

      for (let i = 0; i < elements.length; i++) {
        const element = elements[i];
        const attachment: IEpticaExctractAttachment = {
          title: '',
          source: '',
          filename: '',
          file: {},
          action: IEpticaExtractAttachmentAction.NONE,
        };
        const name = $(element).find('span[data-dz-name]').text().toString();
        const source = element.getAttribute('href');
        let fileSize = $(element).find('span[data-dz-size]').text().toString();
        const nodes = element.querySelector('span[data-dz-size]')?.childNodes || [];
        let size = 0;
        if (nodes.length > 0) {
          size = Number.parseFloat($(nodes[0]).text().toString().trim());
          const type = $(nodes[1]).text().toString().trim();
          const units = ['K', 'M', 'G', 'T', 'P'];
          const index = units.indexOf(type[0]);
          if (index !== -1) {
            size *= Math.pow(1000, index + 1);
          }
        }
        if (source && name) {
          attachment.source = source;
          attachment.filename = name;
          attachment.title = name;
          if (fileSize) {
            attachment.title += ' - ' + fileSize;
            attachment.sizeInFrame = fileSize;
          }
          attachment.action = IEpticaExtractAttachmentAction.ADDED_BY_DRAFT_LOADING;
          attachment.file = {
            name: name,
            status: 'added',
            _removeLink: $(element).find('.dz-remove').get(0),
          };
          if (size !== 0) {
            attachment.file.size = size;
          }
          attachments.push(attachment);
        }
      }
    }

    return attachments;
  }

  static isMessageTextEmpty(frame: HTMLIFrameElement): boolean {
    let myFrame = window.parent.frames[frame.name];
    // get eptica editor
    if (myFrame && myFrame.getHtmlEditor) {
      let oEditor = myFrame.getHtmlEditor(myFrame);
      if (oEditor) {
        // clear the eptica text area
        let data = oEditor.getData();
        return !data;
      }
    }

    return true;
  }

  private static getEpticaDataRequest(requestData: any): Option<EpticaDataRequest> {
    return Option.of(requestData)
      .filter(data => Option.of(data.request).isDefined())
      .map(data => ({
        id: data.request.id,
        accountResponsible: data.request.accountResponsible,
        customer: Option.of(data.customer)
          .flatMap(customer => Option.of(customer.fields as any[]))
          .flatMap(fields => Option.of(fields.filter(field => field.name === EMAIL_CUSTOMER_FIELD).shift()))
          .map(field => ({
            email: Option.of(field.value),
          })),
        lastContact: Option.of(data.request.fields.find(f => f.name == 'wlLastContact')).map(f => f.value),
      }));
  }

  private static getEpticaDataAccount(accountData: any): string | undefined {
    return accountData && accountData.id;
  }

  private static generateHtmlForwardSkill($i18next: i18n, key: string, skills: any[]) {
    return (
      '<div class="request_history_info_content_user_comment">' +
      $i18next.t(key) +
      '<div class="comment_content_text"><p>' +
      skills.map(s => s.name).toString() +
      '</p></div></div>'
    );
  }
}
